import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import {
  ArrowUpDown,
  DollarSign,
  Download,
  Eye,
  Filter,
  Loader2,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  TrendingDown,
  TrendingUp,
  UserX,
  Users,
} from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { User, UserFilters, UserQuery } from '@/types/user'
import type { AxiosError } from 'axios'
import type { ErrorResponse } from '@/types/common'
import type { ExcelColumn } from '@/lib/excel-export'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PaginationNext, PaginationPrevious } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import TitleHeader from '@/components/TitleHeader'
import {
  useDeleteUser,
  useExportUsers,
  useUserStats,
  useUsers,
} from '@/hooks/useUsers'
import AddUserModal from '@/components/Users/AddUserModal'
import DeleteUserModal from '@/components/Users/DeleteUserModal'
import { userService } from '@/services/users'
import { ExcelExporter } from '@/lib/excel-export'

export const Route = createFileRoute('/_authenticated/users/')({
  component: UsersManagement,
})

// Loading skeleton component for table rows
const TableSkeleton = () => (
  <>
    {[...Array(10)].map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Skeleton className="h-4 w-4 rounded" />
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div>
              <Skeleton className="h-4 w-24 mb-1" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-32" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-20" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-20" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-12" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-6 w-16 rounded-full" />
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Skeleton className="h-8 w-8 rounded" />
            <Skeleton className="h-8 w-8 rounded" />
          </div>
        </TableCell>
      </TableRow>
    ))}
  </>
)

function UsersManagement() {
  const [searchFilters, setSearchFilters] = useState<UserFilters>({
    customerId: '',
    username: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    ipAddress: '',
    utmSource: '',
    status: '',
    role: '',
    isEmailVerified: '',
    isPhoneVerified: '',
    createdFrom: '',
    createdTo: '',
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)
  const [appliedFilters, setAppliedFilters] =
    useState<UserFilters>(searchFilters)
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<{
    id: string
    userName: string
    email: string
  } | null>(null)
  const [selectedUser, setSelectedUser] = useState<Partial<User> | null>(null)

  // Convert filters to API query
  const apiQuery: UserQuery = {
    page: currentPage,
    take: pageSize,
    sorts: 'createdAt.DESC',
    isSelectAll: false,
    searchText:
      appliedFilters.username ||
      appliedFilters.email ||
      appliedFilters.fullName ||
      undefined,
    customerId: appliedFilters.customerId || undefined,
    username: appliedFilters.username || undefined,
    fullName: appliedFilters.fullName || undefined,
    email: appliedFilters.email || undefined,
    phoneNumber: appliedFilters.phoneNumber || undefined,
    ipAddress: appliedFilters.ipAddress || undefined,
    utmSource: appliedFilters.utmSource || undefined,
    status: appliedFilters.status || undefined,
    role: appliedFilters.role || undefined,
    isEmailVerified:
      appliedFilters.isEmailVerified === 'true'
        ? true
        : appliedFilters.isEmailVerified === 'false'
          ? false
          : undefined,
    isPhoneVerified:
      appliedFilters.isPhoneVerified === 'true'
        ? true
        : appliedFilters.isPhoneVerified === 'false'
          ? false
          : undefined,
    createdFrom: appliedFilters.createdFrom || undefined,
    createdTo: appliedFilters.createdTo || undefined,
  }

  // Fetch data from API
  const {
    data: usersResponse,
    isLoading: usersLoading,
    isFetching: usersFetching,
    error: usersError,
    refetch: refetchUsers,
  } = useUsers(apiQuery)
  const { data: statsResponse, isLoading: statsLoading } = useUserStats()

  // Mutations
  const deleteUserMutation = useDeleteUser()
  // const toggleStatusMutation = useToggleUserStatus()
  const exportUsersMutation = useExportUsers()

  // Be resilient to various response shapes: {data:{items,meta}} | {items,meta} | []
  const usersItems =
    (usersResponse as any)?.data?.items ??
    (usersResponse as any)?.items ??
    usersResponse ??
    []
  const users: any[] = Array.isArray(usersItems) ? usersItems : []
  const pagination = (usersResponse as any)?.data?.meta ??
    (usersResponse as any)?.meta ?? {
      total: 0,
      page: currentPage,
      limit: pageSize,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
    }
  // Stats can be {data: {...}} or plain object
  const stats = statsResponse?.data ?? statsResponse

  const handleSearch = useCallback(
    (overrides?: Partial<UserFilters>) => {
      const filtersToApply = { ...searchFilters, ...overrides }
      setAppliedFilters(filtersToApply)
      setCurrentPage(1)
    },
    [searchFilters],
  )

  const clearFilters = useCallback(() => {
    const emptyFilters: UserFilters = {
      customerId: '',
      username: '',
      fullName: '',
      email: '',
      phoneNumber: '',
      ipAddress: '',
      utmSource: '',
      status: '',
      role: '',
      isEmailVerified: '',
      isPhoneVerified: '',
      createdFrom: '',
      createdTo: '',
    }
    setSearchFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    setCurrentPage(1)
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const handleDeleteUser = useCallback((user: any) => {
    setUserToDelete({
      id: user.id,
      userName: user.userName,
      email: user.email,
    })
    setIsDeleteUserModalOpen(true)
  }, [])

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteUserModalOpen(false)
    setUserToDelete(null)
  }, [])

  // const handleToggleStatus = useCallback((userId: string, currentStatus: boolean) => {
  //   const newStatus = currentStatus ? 'banned' : 'active'
  //   toggleStatusMutation.mutate({ id: userId, status: newStatus })
  // }, [toggleStatusMutation])

  const mutationExport = useMutation({
    mutationFn: () => {
      return userService.getUsers({
        page: 1,
        take: 100,
        isSelectAll: true,
      })
    },
    onSuccess: (res: any) => {
      const data = res.items.map((item: any) => ({
        username: item.userName,
        email: item.email,
        balanceMinor: formatWalletAmount(
          item.wallet?.balanceMinor,
          item.wallet?.currency,
        ),
        totalTopUpAmountMinor: formatWalletAmount(
          item.wallet?.totalTopUpAmountMinor,
          item.wallet?.currency,
        ),
        discount: item.discount,
        status: item.status,
      }))

      ExcelExporter.export(data, userExcelColumns, {
        filename: `danh-sach-nguoi-dung-${new Date().toISOString().split('T')[0]}.xlsx`,
        sheetName: 'Danh sách người dùng',
      })
      toast.success('Export Excel thành công!')
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message || 'Export thất bại!')
    },
  })

  const userExcelColumns: ExcelColumn[] = [
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'balanceMinor', label: 'Số dư khả dụng' },
    { key: 'totalTopUpAmountMinor', label: 'Tổng nạp' },
    { key: 'discount', label: 'Chiếc khấu' },
    { key: 'status', label: 'Trạng thái' },
  ]

  const handleExportUsers = () => {
    mutationExport.mutate()
  }

  const getStatusBadge = (user: any) => {
    const status = user.status || 'inactive'

    switch (status) {
      case 'active':
        return (
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-xs font-medium bg-green-100 text-green-800 border-green-200"
          >
            Active
          </Badge>
        )
      case 'banned':
        return (
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-xs font-medium bg-red-100 text-red-800 border-red-200"
          >
            Banned
          </Badge>
        )
      case 'unverified':
        return (
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 border-yellow-200"
          >
            Unverified
          </Badge>
        )
      case 'inactive':
      default:
        return (
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-xs font-medium bg-gray-100 text-gray-800 border-gray-200"
          >
            Inactive
          </Badge>
        )
    }
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  // Format amounts based on wallet currency and minor units
  const formatWalletAmount = (
    minor: number | undefined,
    currency: string | null | undefined,
  ) => {
    const amountMinor = minor ?? 0
    const curr = (currency || 'USD').toUpperCase()
    if (curr === 'VND') {
      // VND has 0 decimals; backend provides minor units already as integer VND
      return `${new Intl.NumberFormat('vi-VN').format(amountMinor)} đ`
    }
    // Assume minor = cents for non-VND
    const normal = amountMinor / 100
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: curr,
      }).format(normal)
    } catch {
      return `${normal.toFixed(2)} ${curr}`
    }
  }

  const headerActions = () => (
    <div className="flex gap-3">
      <Button
        variant="outline"
        className="flex items-center gap-2"
        onClick={handleExportUsers}
        disabled={exportUsersMutation.isPending}
      >
        {exportUsersMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        Xuất dữ liệu
      </Button>
      <Button
        className="flex items-center gap-2"
        onClick={() => setIsAddUserModalOpen(true)}
      >
        <Plus className="h-4 w-4" />
        Thêm thành viên
      </Button>
    </div>
  )

  return (
    <div className="p-6">
      <TitleHeader
        title="Quản lý người dùng"
        subtitle="Chào mừng bạn đến với Trung tâm thông tin dữ liệu của bạn!"
        action={headerActions()}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 mt-6">
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Tổng thành viên
                </p>
                {statsLoading ? (
                  <Skeleton className="h-8 w-24 mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(stats?.totalUsers || 0)}
                  </p>
                )}
                {stats && (
                  <div
                    className={`flex items-center gap-1 text-sm mt-1 ${
                      (stats.totalUsersGrowth || 0) >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {(stats.totalUsersGrowth || 0) >= 0 ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <TrendingDown className="h-4 w-4" />
                    )}
                    <span>
                      {Math.abs(stats.totalUsersGrowth || 0)}% so với tháng
                      trước
                    </span>
                  </div>
                )}
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Số dư còn lại
                </p>
                {statsLoading ? (
                  <Skeleton className="h-8 w-32 mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.totalBalance?.vnd
                      ? `${new Intl.NumberFormat('vi-VN').format(stats.totalBalance.vnd)} đ`
                      : '0 đ'}
                  </p>
                )}
                {stats && (
                  <div
                    className={`flex items-center gap-1 text-sm mt-1 ${
                      (stats.totalBalanceGrowth || 0) >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {(stats.totalBalanceGrowth || 0) >= 0 ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <TrendingDown className="h-4 w-4" />
                    )}
                    <span>
                      {Math.abs(stats.totalBalanceGrowth || 0)}% so với tháng
                      trước
                    </span>
                  </div>
                )}
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                <UserX className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Thành viên bị cấm
                </p>
                {statsLoading ? (
                  <Skeleton className="h-8 w-16 mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {stats?.bannedUsers || 0}
                  </p>
                )}
                {stats && (
                  <div
                    className={`flex items-center gap-1 text-sm mt-1 ${
                      (stats.bannedUsersGrowth || 0) >= 0
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}
                  >
                    {(stats.bannedUsersGrowth || 0) >= 0 ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <TrendingDown className="h-4 w-4" />
                    )}
                    <span>
                      {Math.abs(stats.bannedUsersGrowth || 0)}% so với tháng
                      trước
                    </span>
                  </div>
                )}
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>

      <Card className="mt-6 shadow-none">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            Danh sách thành viên
          </CardTitle>
        </CardHeader>

        <Separator />

        <CardContent className="p-0">
          <div className="filter mb-5 px-4">
            <div className="grid grid-cols-4 gap-4 mb-5">
              <Input
                placeholder="ID khách hàng"
                value={searchFilters.customerId}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    customerId: e.target.value,
                  }))
                }
              />
              <Input
                placeholder="Username"
                value={searchFilters.username}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    username: e.target.value,
                  }))
                }
              />
              <Input
                placeholder="Full name"
                value={searchFilters.fullName}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    fullName: e.target.value,
                  }))
                }
              />
              <Input
                placeholder="Email"
                value={searchFilters.email}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-4 gap-4 mb-5">
              <Input
                placeholder="Địa chỉ IP"
                value={searchFilters.ipAddress}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    ipAddress: e.target.value,
                  }))
                }
              />
              <Input
                placeholder="UTM_Source"
                value={searchFilters.utmSource}
                onChange={(e) =>
                  setSearchFilters((prev) => ({
                    ...prev,
                    utmSource: e.target.value,
                  }))
                }
              />
              <Select
                value={searchFilters.status || undefined}
                onValueChange={(value) => {
                  setSearchFilters((prev) => ({ ...prev, status: value }))
                  handleSearch({ status: value })
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Trạng thái" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="banned">Banned</SelectItem>
                  <SelectItem value="unverified">Unverified</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Button
                variant="outline"
                className="mr-3"
                onClick={() => handleSearch()}
                disabled={usersLoading}
              >
                {usersLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Tìm kiếm
              </Button>
              <Button variant="outline" className="mr-3" onClick={clearFilters}>
                <Filter className="h-4 w-4 mr-2" />
                Xoá bộ lọc
              </Button>
              <Button
                variant="outline"
                onClick={() => refetchUsers()}
                disabled={usersLoading || usersFetching}
              >
                <RefreshCw
                  className={
                    usersLoading || usersFetching ? 'animate-spin' : ''
                  }
                />
              </Button>
            </div>
          </div>

          <Table className="">
            <TableHeader className="bg-gray-100">
              <TableRow>
                <TableHead className="w-12">
                  <input type="checkbox" className="rounded border-gray-300" />
                </TableHead>
                <TableHead className="w-[100px]">Username</TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Email
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Số dư khả dụng
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Tổng nạp
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Chiếc khấu
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    Trạng thái
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  </div>
                </TableHead>
                <TableHead>Thao tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersLoading ? (
                <TableSkeleton />
              ) : usersError ? (
                <TableRow>
                  <TableCell colSpan={8} className="px-4 py-12 text-center">
                    <div className="text-red-600">
                      <p>Có lỗi xảy ra khi tải dữ liệu người dùng</p>
                      <Button
                        variant="outline"
                        onClick={() => window.location.reload()}
                        className="mt-4"
                      >
                        Thử lại
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-gray-500">Không có người dùng nào</p>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                          {user.avatar ? (
                            <img
                              src={user.avatar}
                              alt={user.userName}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            <Users className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-base">
                            {user.userName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID {user.id.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {formatWalletAmount(
                        user.wallet?.balanceMinor,
                        user.wallet?.currency,
                      )}
                    </TableCell>
                    <TableCell>
                      {formatWalletAmount(
                        user.wallet?.totalTopUpAmountMinor,
                        user.wallet?.currency,
                      )}
                    </TableCell>
                    <TableCell>{user.discount || 0}%</TableCell>
                    <TableCell>{getStatusBadge(user)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-gray-200"
                          title="Xem chi tiết"
                          onClick={() => {
                            setSelectedUser(user)
                            setIsAddUserModalOpen(true)
                          }}
                        >
                          <Eye className="h-4 w-4 text-gray-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-gray-200"
                          onClick={() => handleDeleteUser(user)}
                          disabled={deleteUserMutation.isPending}
                          title="Xóa người dùng"
                        >
                          {deleteUserMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-gray-600" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6 px-6 pb-6">
          <div className="text-sm text-gray-500">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center gap-2">
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (pagination.hasPrev) handlePageChange(currentPage - 1)
                }}
                className={
                  !pagination.hasPrev ? 'pointer-events-none opacity-50' : ''
                }
              />
              <span className="text-sm font-medium px-2">
                {pagination.page} / {pagination.totalPages}
              </span>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  if (pagination.hasNext) handlePageChange(currentPage + 1)
                }}
                className={
                  !pagination.hasNext ? 'pointer-events-none opacity-50' : ''
                }
              />
            </div>
          )}
        </div>
      </Card>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => {
          setIsAddUserModalOpen(false)
          setSelectedUser(null)
        }}
        user={selectedUser}
      />

      {/* Delete User Modal */}
      <DeleteUserModal
        isOpen={isDeleteUserModalOpen}
        onClose={handleCloseDeleteModal}
        user={userToDelete}
      />
    </div>
  )
}
